import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import RichTextEditor from "../../../../common/RichTextEditor";
import Sidebar from "../../../../common/Sidebar";
import GeneralAdminSidebar from "../../../../common/GeneralAdminSidebar";
import {
  GeneralAdminRoutes,
  mainLayoutStyles,
  routes,
  showNotification,
} from "../../../../constants";
import { useAuth } from "../../../../hooks";
import { useOrganizationContext } from "../../../../providers/OrganizationAdmin/OrganizationProvider";
import {
  EMAIL_TEMPLATE_SCOPE_GEN,
  EMAIL_TEMPLATE_SCOPE_ORG,
  useCreateEmailTemplate,
  useGetEmailTemplateById,
  useUpdateEmailTemplate,
  useUploadTemplateLogo,
} from "../../hooks/EmailTemplatesHooks";

/**
 * Create / view / edit an email template.
 *
 * Props:
 *  - scope: "org" (default) edits orgEmailTemplates; "gen" edits the standard
 *    templates in genEmailTemplates (General Admin).
 *  - readOnly: view-only (no Edit button). Used by Org Admin to preview a
 *    standard template before adding it. Back returns to the standard list.
 */
const EmailTemplateDetailsContainer = ({
  scope = EMAIL_TEMPLATE_SCOPE_ORG,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { currentUser, organizationId: authOrganizationId } = useAuth();
  const { currentOrganization } = useOrganizationContext() || {};

  const isGen = scope === EMAIL_TEMPLATE_SCOPE_GEN;
  // Standard templates are not tied to an organization
  const organizationId = isGen ? null : authOrganizationId;
  // General Admin manages standard templates; Org Admin only previews them
  const isGeneralAdminView = isGen && !readOnly;

  const listRoute = readOnly
    ? routes.standardEmailTemplates
    : isGen
    ? GeneralAdminRoutes.genEmailTemplates
    : routes.emailTemplates;
  const detailsBasePath = isGen
    ? "/standard-email-templates/details"
    : "/email-templates/details";

  const isCreateMode = !readOnly && (!templateId || templateId === "create");
  const userId = currentUser?.id;

  const { template, loading: fetchLoading } = useGetEmailTemplateById(
    isCreateMode ? null : templateId,
    scope
  );
  const { createTemplate, loading: createLoading } = useCreateEmailTemplate(scope);
  const { updateTemplate, loading: updateLoading } = useUpdateEmailTemplate(scope);
  const { uploadLogo, loading: uploadLoading } = useUploadTemplateLogo();

  const [isEditMode, setIsEditMode] = useState(isCreateMode);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    subject: "",
    status: "active",
    logo: "",
    topSection: "",
    bottomSection: "",
    default: false,
    narratorCanUpdateAudio: true,
    narratorCanUpdatePhoto: false,
    narratorCanUpdateTitle: false,
    narratorCanUpdateDescription: false,
    narratorGreeting: "",
  });

  // Scroll to top when entering edit mode (Quill used to steal focus/scroll)
  useEffect(() => {
    if (isEditMode && !isCreateMode) {
      // Small delay to let the editor render before scrolling
      const timer = setTimeout(() => window.scrollTo(0, 0), 100);
      return () => clearTimeout(timer);
    }
  }, [isEditMode, isCreateMode]);

  // Load template data when fetched
  useEffect(() => {
    if (template && !isCreateMode) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        subject: template.subject || "",
        status: template.status || "active",
        logo: template.logo || "",
        topSection: template.topSection || "",
        bottomSection: template.bottomSection || "",
        narratorCanUpdateAudio:
          typeof template.narratorCanUpdateAudio === "boolean"
            ? template.narratorCanUpdateAudio
            : true,
        narratorCanUpdatePhoto:
          typeof template.narratorCanUpdatePhoto === "boolean"
            ? template.narratorCanUpdatePhoto
            : false,
        narratorCanUpdateTitle:
          typeof template.narratorCanUpdateTitle === "boolean"
            ? template.narratorCanUpdateTitle
            : false,
        narratorCanUpdateDescription:
          typeof template.narratorCanUpdateDescription === "boolean"
            ? template.narratorCanUpdateDescription
            : false,
        narratorGreeting: template.narratorGreeting || "",
        default: template.status === "inactive" ? false : !!template.default,
      });
    }
  }, [template, isCreateMode]);

  // Prefill logo with organization logo when creating a new template
  useEffect(() => {
    if (!isGen && isCreateMode && currentOrganization?.logoOP && !formData.logo) {
      setFormData((prev) => ({
        ...prev,
        logo: currentOrganization.logoOP,
      }));
    }
  }, [isGen, isCreateMode, currentOrganization, formData.logo]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleRichTextChange = (field) => (html) => {
    setFormData((prev) => ({
      ...prev,
      [field]: html,
    }));
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      status: value,
      default: value === "inactive" ? false : prev.default,
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await uploadLogo(file, organizationId);
    if (result.success) {
      setFormData((prev) => ({
        ...prev,
        logo: result.url,
      }));
      showNotification("Logo uploaded successfully");
    } else {
      showNotification("Error uploading logo");
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showNotification("Please enter a template name");
      return;
    }
    if (!formData.subject.trim()) {
      showNotification("Please enter an email subject");
      return;
    }

    const normalizedFormData = {
      ...formData,
      default: formData.status === "inactive" ? false : !!formData.default,
    };

    if (isCreateMode) {
      const result = await createTemplate(normalizedFormData, userId, organizationId);
      if (result.success) {
        showNotification("Template created successfully");
        navigate(`${detailsBasePath}/${result.id}`);
      } else {
        showNotification("Error creating template");
      }
    } else {
      const result = await updateTemplate(
        templateId,
        normalizedFormData,
        userId,
        organizationId
      );
      if (result.success) {
        showNotification("Template updated successfully");
        setIsEditMode(false);
      } else {
        showNotification("Error updating template");
      }
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate(listRoute);
      return;
    }

    setIsEditMode(false);
    if (template) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        subject: template.subject || "",
        status: template.status || "active",
        logo: template.logo || "",
        topSection: template.topSection || "",
        bottomSection: template.bottomSection || "",
        narratorCanUpdateAudio:
          typeof template.narratorCanUpdateAudio === "boolean"
            ? template.narratorCanUpdateAudio
            : true,
        narratorCanUpdatePhoto:
          typeof template.narratorCanUpdatePhoto === "boolean"
            ? template.narratorCanUpdatePhoto
            : false,
        narratorCanUpdateTitle:
          typeof template.narratorCanUpdateTitle === "boolean"
            ? template.narratorCanUpdateTitle
            : false,
        narratorCanUpdateDescription:
          typeof template.narratorCanUpdateDescription === "boolean"
            ? template.narratorCanUpdateDescription
            : false,
        narratorGreeting: template.narratorGreeting || "",
        default: template.status === "inactive" ? false : !!template.default,
      });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "-";
  };

  const isSaving = createLoading || updateLoading;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={
          isCreateMode ? "Create Email Template" : formData.name || "Email Template"
        }
        bgColor="#fff"
        buttonText={isEditMode || readOnly ? undefined : "Edit"}
        editEnabled={isEditMode}
        setEditEnabled={setIsEditMode}
        cancelButton={isEditMode}
        onCancelClick={handleCancel}
        handleSave={handleSave}
        loading={isSaving}
        onBackClick={() => navigate(listRoute)}
        disableEdit={isSaving}
      />

      {isGeneralAdminView ? <GeneralAdminSidebar /> : <Sidebar />}

      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} sx={{ padding: "20px" }}>
          {fetchLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Template Details */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#595959",
                    mb: 3,
                    fontFamily: "Avenir Book, sans-serif",
                  }}
                >
                  Template Details
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <TextField
                    label="Template Name"
                    value={formData.name}
                    onChange={handleInputChange("name")}
                    disabled={!isEditMode}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                      "& .MuiInputLabel-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />

                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={handleInputChange("description")}
                    disabled={!isEditMode}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{
                      "& .MuiInputBase-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                      "& .MuiInputLabel-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />

                  <FormControl fullWidth disabled={!isEditMode}>
                    <InputLabel sx={{ fontFamily: "Avenir Book, sans-serif" }}>
                      Status
                    </InputLabel>
                    <Select
                      value={formData.status}
                      onChange={handleStatusChange}
                      label="Status"
                      sx={{ fontFamily: "Avenir Book, sans-serif" }}
                    >
                      <MenuItem
                        value="active"
                        sx={{ fontFamily: "Avenir Book, sans-serif" }}
                      >
                        Active
                      </MenuItem>
                      <MenuItem
                        value="inactive"
                        sx={{ fontFamily: "Avenir Book, sans-serif" }}
                      >
                        Inactive
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {!isGen && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.default}
                        onChange={(_, checked) =>
                          setFormData((prev) => ({
                            ...prev,
                            default: checked,
                          }))
                        }
                        disabled={!isEditMode || formData.status === "inactive"}
                      />
                    }
                    label="Default Template"
                    sx={{
                      "& .MuiTypography-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />
                  )}
                </Box>
              </Paper>

              {/* Narrator Page Details */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#595959",
                    mb: 3,
                    fontFamily: "Avenir Book, sans-serif",
                  }}
                >
                  Guest Contributor Webpage Details
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      fontSize: "14px",
                      color: "#595959",
                      fontFamily: "Avenir Book, sans-serif",
                    }}
                  >
                    Narrator can update:
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.narratorCanUpdateAudio}
                        onChange={(_, checked) =>
                          setFormData((prev) => ({
                            ...prev,
                            narratorCanUpdateAudio: checked,
                          }))
                        }
                        disabled={!isEditMode}
                      />
                    }
                    label="Audio"
                    sx={{
                      "& .MuiTypography-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.narratorCanUpdatePhoto}
                        onChange={(_, checked) =>
                          setFormData((prev) => ({
                            ...prev,
                            narratorCanUpdatePhoto: checked,
                          }))
                        }
                        disabled={!isEditMode}
                      />
                    }
                    label="Photo"
                    sx={{
                      "& .MuiTypography-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.narratorCanUpdateTitle}
                        onChange={(_, checked) =>
                          setFormData((prev) => ({
                            ...prev,
                            narratorCanUpdateTitle: checked,
                          }))
                        }
                        disabled={!isEditMode}
                      />
                    }
                    label="Title"
                    sx={{
                      "& .MuiTypography-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.narratorCanUpdateDescription}
                        onChange={(_, checked) =>
                          setFormData((prev) => ({
                            ...prev,
                            narratorCanUpdateDescription: checked,
                          }))
                        }
                        disabled={!isEditMode}
                      />
                    }
                    label="Description"
                    sx={{
                      "& .MuiTypography-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />

                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#595959",
                        fontFamily: "Avenir Book, sans-serif",
                        mb: 1,
                      }}
                    >
                      Greeting (supports tokens: {"{First Name}"}, {"{Last Name}"},
                      {"{Username}"}, {"{QuickTale Title}"})
                    </Box>

                    {isEditMode ? (
                      <RichTextEditor
                        value={formData.narratorGreeting}
                        onChange={handleRichTextChange("narratorGreeting")}
                        minHeight="90px"
                      />
                    ) : (
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          p: 2,
                          minHeight: "90px",
                          fontFamily: "Avenir Book, sans-serif",
                          fontSize: "16px",
                          lineHeight: "1.6",
                          "& p": { margin: "0 0 16px 0" },
                          "& p:last-child": { marginBottom: 0 },
                          "& ol, & ul": { paddingLeft: "24px" },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formData.narratorGreeting || "<p>No greeting</p>",
                    }}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>

              {/* Email Details */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#595959",
                    mb: 3,
                    fontFamily: "Avenir Book, sans-serif",
                  }}
                >
                  Email Details
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <TextField
                    label="Email Subject"
                    value={formData.subject}
                    onChange={handleInputChange("subject")}
                    disabled={!isEditMode}
                    required
                    fullWidth
                    sx={{
                      "& .MuiInputBase-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                      "& .MuiInputLabel-root": {
                        fontFamily: "Avenir Book, sans-serif",
                      },
                    }}
                  />

                  <Box>
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#595959",
                        mb: 1,
                        fontFamily: "Avenir Book, sans-serif",
                      }}
                    >
                      Logo
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {formData.logo && (
                        <Box
                          component="img"
                          src={formData.logo}
                          alt="Logo"
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      )}

                      {isEditMode && (
                        <Button
                          variant="outlined"
                          component="label"
                          disabled={uploadLoading}
                          sx={{
                            textTransform: "none",
                            fontFamily: "Avenir Book, sans-serif",
                            borderColor: "#0AB2EE",
                            color: "#0AB2EE",
                          }}
                        >
                          {uploadLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            "Upload Logo"
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleLogoUpload}
                          />
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Top Section - RTF */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#595959",
                    mb: 2,
                    fontFamily: "Avenir Book, sans-serif",
                  }}
                >
                  Top Section (After "Hi [First Name]")
                </Box>

                {isEditMode ? (
                  <RichTextEditor
                    value={formData.topSection}
                    onChange={handleRichTextChange("topSection")}
                    minHeight="100px"
                  />
                ) : (
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      p: 2,
                      minHeight: "100px",
                      fontFamily: "Avenir Book, sans-serif",
                      fontSize: "16px",
                      lineHeight: "1.6",
                      "& p": { margin: "0 0 16px 0" },
                      "& p:last-child": { marginBottom: 0 },
                      "& ol, & ul": { paddingLeft: "24px" },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formData.topSection || "<p>No content</p>",
                    }}
                  />
                )}
              </Paper>

              {/* Bottom Section - RTF */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#595959",
                    mb: 2,
                    fontFamily: "Avenir Book, sans-serif",
                  }}
                >
                  Bottom Section (After share link(s))
                </Box>

                {isEditMode ? (
                  <RichTextEditor
                    value={formData.bottomSection}
                    onChange={handleRichTextChange("bottomSection")}
                    minHeight="100px"
                  />
                ) : (
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      p: 2,
                      minHeight: "100px",
                      fontFamily: "Avenir Book, sans-serif",
                      fontSize: "16px",
                      lineHeight: "1.6",
                      "& p": { margin: "0 0 16px 0" },
                      "& p:last-child": { marginBottom: 0 },
                      "& ol, & ul": { paddingLeft: "24px" },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formData.bottomSection || "<p>No content</p>",
                        }}
                  />
                )}
              </Paper>

              {/* Meta Fields - Read Only */}
              {!isCreateMode && template && (
                <Paper sx={{ p: 3 }}>
                  <Box
                    sx={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#595959",
                      mb: 3,
                      fontFamily: "Avenir Book, sans-serif",
                    }}
                  >
                    Template Information
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          fontSize: "12px",
                          color: "#9e9e9e",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        Created By
                      </Box>
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#595959",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        {template.createdByData?.userName ||
                          template.createdByData?.firstName ||
                          "-"}
                      </Box>
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          fontSize: "12px",
                          color: "#9e9e9e",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        Created At
                      </Box>
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#595959",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        {formatDate(template.createdAt)}
                      </Box>
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          fontSize: "12px",
                          color: "#9e9e9e",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        Updated By
                      </Box>
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#595959",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        {template.updatedByData?.userName ||
                          template.updatedByData?.firstName ||
                          "-"}
                      </Box>
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          fontSize: "12px",
                          color: "#9e9e9e",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        Updated At
                      </Box>
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#595959",
                          fontFamily: "Avenir Book, sans-serif",
                        }}
                      >
                        {formatDate(template.updatedAt)}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              )}
            </>
          )}
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={fetchLoading || isSaving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default EmailTemplateDetailsContainer;
